// Onboarding feedback surveys (Phase 1f) — the W1/M1/M3/W10 schedule shown on an
// onboarding card. Completing the Week10 survey records the trial decision.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { PrimaryBtn, OutlineBtn, Pill } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

const RECS = ['Extend', 'End', 'Confirm'];

export default function OnboardingSurveys({ onboardingId, showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // { id, kind, title, comment, recommendation }
  const [busy, setBusy] = useState(false);

  const load = () => api.onboardingSurveys(onboardingId).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onboardingId]);

  const submit = async () => {
    if (busy) return;
    if (form.kind === 'Week10' && !form.recommendation) { showToast?.('Pick a trial decision'); return; }
    setBusy(true);
    try {
      await api.completeSurvey(form.id, { responses: { comment: form.comment || '' }, recommendation: form.recommendation });
      showToast?.('Survey completed');
      setForm(null); await load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!rows || rows.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${AG.divider}` }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: AG.n400, marginBottom: 8 }}>Feedback surveys</div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        {rows.map((s) => {
          const overdue = s.status === 'Pending' && s.dueDate <= today;
          return (
            <button key={s.id} type="button" onClick={() => s.status === 'Pending' && setForm({ id: s.id, kind: s.kind, title: s.title, comment: '', recommendation: '' })}
              title={s.title}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, border: `1px solid ${s.status === 'Completed' ? AG.success : overdue ? AG.warning : AG.n300}`, background: s.status === 'Completed' ? 'rgba(16,185,129,.1)' : '#fff', color: s.status === 'Completed' ? AG.successDark : AG.n700, fontSize: 12, fontWeight: 600, cursor: s.status === 'Pending' ? 'pointer' : 'default' }}>
              {s.kind}
              <span style={{ fontWeight: 400, color: AG.n400 }}>· {s.status === 'Completed' ? (s.recommendation || 'done') : `due ${s.dueDate}`}</span>
            </button>
          );
        })}
      </div>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.title}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              {form.kind === 'Week10' && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Trial decision</span>
                  <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
                    {RECS.map((r) => (
                      <button key={r} type="button" onClick={() => setForm({ ...form, recommendation: r })}
                        style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${form.recommendation === r ? AG.primary : AG.n300}`, background: form.recommendation === r ? AG.primaryLightest : '#fff', color: form.recommendation === r ? AG.primaryDark : AG.n600, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{r}</button>
                    ))}
                  </div>
                </div>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Notes</span>
              <TextField fullWidth size="small" multiline minRows={2} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} sx={{ mt: 0.5 }} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={submit} disabled={busy}>Complete</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
